/*
  Based on https://codesandbox.io/s/9j897k0mwy, treat it as vendor code.
 */

import React, { Fragment, PureComponent } from 'react'
import { DragDropContext } from 'react-dnd'
import HTML5Backend from 'react-dnd-html5-backend'

import ImageWrapper from './Components/ImageWrapper'
import ImageDragLayer from './Components/ImageDragLayer'
import Image from './Components/Image'

const getNodeClientBounds = node => {
  const el = node.nodeType === 1 ? node : node.parentElement
  if (!el) {
    return null
  }
  return el.getBoundingClientRect()
}

class ImageSorting extends PureComponent {
  constructor(props) {
    super(props)

    this.state = {
      selectedImages: [],
      selectedImagesIds: [],
      draggedImagesIds: [],
      insertIndex: -1,
      images: this.props.images.map((image, index) => ({ ...image, position: index, ...this.props.imageProps || {} })),
    }

    this.onImageMove = this.onImageMove.bind(this)
    this.onImageDragStart = this.onImageDragStart.bind(this)
    this.onImageDragComplete = this.onImageDragComplete.bind(this)
    this.onImageSelectionChange = this.onImageSelectionChange.bind(this)
  }

  onImageDragStart(dragItem) {
    const images = this.state.images.slice()

    Array.from(this.container.childNodes).map((child, i) => {
      if (images[i]) {
        images[i].bounds = getNodeClientBounds(child)
      }
    })

    this.props.onDragStateChange({ isActive: true })

    this.setState({
      images,
      selectedImages: dragItem.images,
      selectedImagesIds: dragItem.images.map(c => c.id),
      draggedImagesIds: dragItem.images.map(c => c.id),
      hoveredImageId: dragItem.draggedImage.id,
      activeImageId: dragItem.draggedImage.id,
    })
  }

  onImageMove(dragItem, hoverId, pointerOffset) {
    const dragId = dragItem.draggedImage.id

    const images = this.state.images.slice()

    const dragIndex = images.findIndex(el => el.id === dragId)
    const hoverIndex = images.findIndex(el => el.id === hoverId)
    const dragImage = images[dragIndex]
    const hoverImage = images[hoverIndex]

    if (!hoverImage || !hoverImage.bounds) {
      return
    }

    const midX = hoverImage.bounds.left + (hoverImage.bounds.right - hoverImage.bounds.left) / 2
    const insertIndex = pointerOffset.x < midX ? hoverIndex : hoverIndex + 1

    if (
      this.previousDragId === dragId
      && this.previousHoverId === hoverId
      && this.previousInsertIndex === insertIndex
    ) {
      return
    }
    this.previousDragId = dragId
    this.previousHoverId = hoverId
    this.previousInsertIndex === insertIndex

    this.setState({
      insertIndex,
      hoveredImageIndex: hoverIndex,
      hoveredImageId: hoverId,
    })
  }

  onImageDragComplete(dragItem) {
    const changes = {
      draggedImagesIds: [],
      insertIndex: -1,
      hoveredImageId: null,
      hoveredImageIndex: -1,
    }
    if (dragItem) {
      const images = this.state.images.slice()
      const draggedImages = dragItem.images
      const remainingImages = images.filter(c => !draggedImages.find(dc => dc.id === c.id))
      let insertIndex = -1
      if (this.state.insertIndex < images.length) {
        let index = this.state.insertIndex
        do {
          if (images[index]) {
            const imageIdAtInsertIndex = images[index].id
            insertIndex = remainingImages.findIndex(c => c.id === imageIdAtInsertIndex)
          }

          index += 1
        } while (insertIndex < 0 && index < images.length)
      }
      if (insertIndex < 0) {
        insertIndex = remainingImages.length
      }
      remainingImages.splice(insertIndex, 0, ...draggedImages)
      changes.images = remainingImages
    }

    this.props.onChange(changes.images.map((image) => image.id))
    this.props.onDragStateChange({ isActive: false })
    this.setState(changes)
  }

  onImageSelectionChange(imageId, cmdKeyActive, shiftKeyActive) {
    let selectedImagesIds = []
    let activeImageId

    const images = this.state.images.slice()
    const previousSelectedImagesIds = this.state.selectedImagesIds.slice()
    const previousActiveImageId = this.state.activeImageId

    if (cmdKeyActive) {
      if (previousSelectedImagesIds.indexOf(imageId) > -1 && imageId !== previousActiveImageId) {
        selectedImagesIds = previousSelectedImagesIds.filter(id => id !== imageId)
      } else {
        selectedImagesIds = [...previousSelectedImagesIds, imageId]
      }
    } else if (shiftKeyActive && imageId !== previousActiveImageId) {
      const activeImageIndex = images.findIndex(c => c.id === previousActiveImageId)
      const imageIndex = images.findIndex(c => c.id === imageId)
      const lowerIndex = Math.min(activeImageIndex, imageIndex)
      const upperIndex = Math.max(activeImageIndex, imageIndex)
      selectedImagesIds = images.slice(lowerIndex, upperIndex + 1).map(c => c.id)
    } else {
      selectedImagesIds = [imageId]
      activeImageId = imageId
    }

    const selectedImages = images.filter(c => selectedImagesIds.includes(c.id))

    const changes = {
      selectedImages,
      selectedImagesIds,
    }
    if (activeImageId) {
      changes.activeImageId = activeImageId
    }

    this.props.onSelectionChange(changes.selectedImagesIds)
    this.setState(changes)
  }

  render() {
    const {
      images,
      draggedImagesIds,
      selectedImages,
      selectedImagesIds,
      activeImageId,
      hoveredImageId,
      hoveredImageIndex,
      insertIndex,
    } = this.state

    return (
      <main>
        <ImageDragLayer imageComponent={this.props.imageComponent} imageProps={this.props.imageProps} />
        <div
          className={this.props.className}
          ref={el => {
            this.container = el
          }}
        >
          {images.map((image, i) => {
            const prevImage = i > 0 ? images[i - 1] : null
            const nextImage = i < images.length ? images[i + 1] : null

            const isDragging = draggedImagesIds.includes(image.id)
            const isDraggingPrevImage = !!prevImage && draggedImagesIds.includes(prevImage.id)
            const isDraggingNextImage = !!nextImage && draggedImagesIds.includes(nextImage.id)

            const shouldInsertLineOnLeft = !isDragging && !isDraggingPrevImage && hoveredImageIndex === i && insertIndex === i
            const shouldInsertLineOnRight = !isDragging && !isDraggingNextImage && hoveredImageIndex === i && insertIndex === i + 1

            const shouldInsertLineOnRightOfPrevImage = !!prevImage && !isDraggingPrevImage && hoveredImageIndex === i - 1 && insertIndex === i
            const shouldInsertLineOnLeftOfNextImage = !!nextImage
                    && !isDraggingNextImage
                    && hoveredImageIndex === i + 1
                    && insertIndex === i + 1

            const isHovered = hoveredImageId === image.id
                    || shouldInsertLineOnRightOfPrevImage
                    || shouldInsertLineOnLeftOfNextImage

            return (
              <Fragment key={i}>
                {shouldInsertLineOnLeft && this.props.spaceComponent}
                <div
                  key={`image-div-${image.id}`}
                  style={{ position: 'relative' }}
                  className={`${this.props.containerSizes} u-mb10 ${i === 0 ? 'thumbnail-container' : ''}`}
                >
                  <ImageWrapper
                    key={`image-wrapper-${image.id}`}
                    isDragging={isDragging}
                    isActive={activeImageId === image.id}
                    isHovered={isHovered}
                    isSelected={selectedImagesIds.includes(image.id)}
                  >
                    <Image
                      key={`image-${image.id}`}
                      imageComponent={this.props.imageComponent}
                      id={image.id}
                      url={image.url}
                      position={image.position}
                      imageProps={{ ...this.props.images.find(img => img.id === image.id), ...this.props.imageProps || {} }}
                      selectedImages={selectedImages}
                      onRemove={this.props.onRemove}
                      onMove={this.onImageMove}
                      onDragStart={this.onImageDragStart}
                      onDragComplete={this.onImageDragComplete}
                      onSelectionChange={this.onImageSelectionChange}
                    />
                  </ImageWrapper>
                </div>
                {shouldInsertLineOnRight && this.props.spaceComponent}
              </Fragment>
            )
          })}
          {this.props.extraItems}
        </div>
      </main>
    )
  }
}

export { ImageSorting as RawImageSorting }
export default DragDropContext(HTML5Backend)(ImageSorting)
